import { useStore } from 'vuex';
import { REPORT_TYPES } from '../config/reportConfig.js';
import moment from 'moment';

export function useReportData() {
  const store = useStore();
  
  // Map report types to their corresponding store actions
  const reportActions = {
    [REPORT_TYPES.CALLS_REPORT]: 'GetCallReports',
    [REPORT_TYPES.TOTAL_PERFORMANCE]: 'GetTotalPerformance',
    [REPORT_TYPES.PERFORMANCE_BY_PERIOD]: 'GetPerformanceByPeriod',
    [REPORT_TYPES.SYSTEM_INBOUND]: 'GetSystemInbound',
    [REPORT_TYPES.AGENT_LEAVING_DETAIL]: 'GetLeavingDetails',
    [REPORT_TYPES.AGENT_LEAVING_SUMMARY]: 'GetLeavingSummary',
    [REPORT_TYPES.AGENT_LOGIN_DETAIL]: 'GetLoginDetails',
    [REPORT_TYPES.AGENT_LOGIN_SUMMARY]: 'GetLoginSummary',
    [REPORT_TYPES.AGENT_STATUS_DETAIL]: 'GetStatusDetails',
    [REPORT_TYPES.AGENT_STATUS_SUMMARY]: 'GetStatusSummary',
    [REPORT_TYPES.KPI_REPORT]: 'GetAgentsKPIs',
    [REPORT_TYPES.CSAT_REPORT]: 'GetCSATReport',
    [REPORT_TYPES.REPEATED_CALLS_REPORT]: 'GetRepeatedCallsReport',
    [REPORT_TYPES.CALL_REASONS_REPORT]: 'GetCallReasonsReport',
    [REPORT_TYPES.IVR_REPORT]: 'GetIVRReport',
    [REPORT_TYPES.CSAT_TABLE_REPORT]: 'GetCSATTableReport',
    [REPORT_TYPES.VOICE_AGENT_CDR]: 'GetVoiceCDRList',
    [REPORT_TYPES.AI_CHAT_TRANSCRIPT]: 'GetAIChatList'
  };
  
  // Map report types to their data extraction functions
  const dataExtractors = {
    [REPORT_TYPES.CALLS_REPORT]: (response) => ({
      data: response.calls,
      count: response.count
    }),
    [REPORT_TYPES.TOTAL_PERFORMANCE]: (response) => ({
      data: response.records,
      count: response.records?.length || 0
    }),
    [REPORT_TYPES.PERFORMANCE_BY_PERIOD]: (response) => ({
      data: {
        periods: response.periods || [],
        total: response.total || null
      },
      count: response.periods?.length || 0
    }),
    [REPORT_TYPES.SYSTEM_INBOUND]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.AGENT_LEAVING_DETAIL]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.AGENT_LEAVING_SUMMARY]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.AGENT_LOGIN_DETAIL]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.AGENT_LOGIN_SUMMARY]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.AGENT_STATUS_DETAIL]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.AGENT_STATUS_SUMMARY]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.KPI_REPORT]: (response) => ({
      data: response.records,
      count: response.records?.length || 0
    }),
    [REPORT_TYPES.CSAT_REPORT]: (response) => ({
      data: response.ratings || {},
      count: 1 // CSAT reports are not paginated, always return count 1
    }),
    [REPORT_TYPES.REPEATED_CALLS_REPORT]: (response) => ({
      data: response.records,
      count: response.count
    }),
    [REPORT_TYPES.CALL_REASONS_REPORT]: (response) => ({
      data: response.result || response.data || {},
      count: 1
    }),
    [REPORT_TYPES.IVR_REPORT]: (response) => {
      // The API returns result.records as an array of root nodes
      // Return array directly - component will handle stacking
      const records = response.result?.records || [];
      
      if (records.length === 0) {
        return { data: null, count: 0 };
      } else if (records.length === 1) {
        // Single root node - return it directly
        return { data: records[0], count: 1 };
      } else {
        // Multiple root nodes - return as array for vertical stacking
        return { data: records, count: records.length };
      }
    },
    [REPORT_TYPES.CSAT_TABLE_REPORT]: (response) => ({
      data: response.result?.records || {},
      count: Object.keys(response.result?.records || {}).length
    }),
    [REPORT_TYPES.VOICE_AGENT_CDR]: (response) => ({
      data: response.items || [],
      count: response.count || 0
    }),
    [REPORT_TYPES.AI_CHAT_TRANSCRIPT]: (response) => ({
      data: response.items || [],
      count: response.count || 0
    })
  };
  
  const fetchReportData = async (reportType, payload, stateManager) => {
    const actionName = reportActions[reportType];
    const dataExtractor = dataExtractors[reportType];
    
    if (!actionName || !dataExtractor) {
      throw new Error(`Unsupported report type: ${reportType}`);
    }
    
    try {
      stateManager.setLoading(true);
      
      // Validate date range
      if (payload.from && payload.to) {
        const fromDate = moment(payload.from, "YYYY-MM-DD HH:mm:ss", true);
        const toDate = moment(payload.to, "YYYY-MM-DD HH:mm:ss", true);
        
        if (fromDate.isAfter(toDate)) {
          const errorMessage = 'Invalid date range: "From" date cannot be after "To" date';
          stateManager.setLoading(false);
          // Use setTimeout to re-set message after a short delay to ensure it persists
          setTimeout(() => {
            store.dispatch('SetMessageDialog', {
              show: true,
              success: false,
              message: errorMessage,
            });
          }, 200);
          return { success: false, message: errorMessage };
        }
        
        // Validate that dates are in the same year
        if (fromDate.year() !== toDate.year()) {
          const errorMessage = 'Invalid date range: Dates must be within the same year';
          stateManager.setLoading(false);
          // Set message dialog after loading is set to false to ensure it persists
          store.dispatch('SetMessageDialog', {
            show: true,
            success: false,
            message: errorMessage,
          });
          // Use setTimeout to re-set message after a short delay to ensure it persists
          setTimeout(() => {
            store.dispatch('SetMessageDialog', {
              show: true,
              success: false,
              message: errorMessage,
            });
          }, 200);
          return { success: false, message: errorMessage };
        }
      }
      
      // Validate that required filters are present
      const requiredFilters = validateRequiredFilters(reportType, payload);
      if (!requiredFilters.valid) {
        throw new Error(`Missing required filters for report type ${reportType}: ${requiredFilters.missing.join(', ')}`);
      }
      
      stateManager.clearError();
      
      const response = await store.dispatch(actionName, payload);
      
      // Check if response is undefined or null
      if (!response) {
        throw new Error('No response received from server');
      }
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch report data');
      }
      
      // Handle export case
      if (payload.export) {
        return { success: true, exported: true };
      }
      
      const { data, count } = dataExtractor(response);
      
      // Ensure data and count are valid
      const safeData = data || [];
      const safeCount = typeof count === 'number' ? count : (Array.isArray(data) ? data.length : 0);
      
      stateManager.setData(safeData, safeCount, payload.iscount === 1);
      
      return { success: true, data: safeData, count: safeCount };
      
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred while fetching data';
      stateManager.setError(errorMessage);
      throw error;
    } finally {
      stateManager.setLoading(false);
    }
  };
  
  const fetchReportDataWithRetry = async (reportType, payload, stateManager, maxRetries = 3) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fetchReportData(reportType, payload, stateManager);
      } catch (error) {
        lastError = error;
        
        // Don't retry on validation errors or user errors
        if (error.response?.status >= 400 && error.response?.status < 500) {
          break;
        }
        
        // Wait before retrying (exponential backoff)
        if (attempt < maxRetries) {
          await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 1000));
        }
      }
    }
    
    throw lastError;
  };
  
  const validateRequiredFilters = (reportType, payload) => {
    const missing = [];
    
    // All reports require date range
    if (!payload.from || !payload.to) {
      missing.push('date range (from/to)');
    }
    
    // Check report-specific required filters
    switch (reportType) {
      case REPORT_TYPES.CALLS_REPORT:
        // Calls report requires callType, agent, hungparty
        // These can be 'All', '0', or any valid value
        if (payload.callType == null || payload.callType === undefined || payload.callType === '') missing.push('callType');
        if (payload.agent == null || payload.agent === undefined || payload.agent === '') missing.push('agent');
        if (payload.hungparty == null || payload.hungparty === undefined || payload.hungparty === '') missing.push('hungparty');
        break;
        
      case REPORT_TYPES.PERFORMANCE_BY_PERIOD:
        // Performance by period requires skill and period
        if (payload.skill === null || payload.skill === undefined || payload.skill === '') missing.push('skill');
        if (!payload.period) missing.push('period');
        break;
        
      case REPORT_TYPES.SYSTEM_INBOUND:
        // System inbound requires skill
        if (payload.skill === null || payload.skill === undefined || payload.skill === '') missing.push('skill');
        break;
        
      case REPORT_TYPES.AGENT_LEAVING_DETAIL:
      case REPORT_TYPES.AGENT_LEAVING_SUMMARY:
      case REPORT_TYPES.AGENT_LOGIN_DETAIL:
      case REPORT_TYPES.AGENT_LOGIN_SUMMARY:
      case REPORT_TYPES.AGENT_STATUS_DETAIL:
      case REPORT_TYPES.AGENT_STATUS_SUMMARY:
        // Agent reports require agent
        if (payload.agent == null || payload.agent === undefined || payload.agent === '') missing.push('agent');
        break;
        
      case REPORT_TYPES.CSAT_REPORT:
        // CSAT report requires ratingType
        if (!payload.ratingType) missing.push('ratingType');
        break;
        
      case REPORT_TYPES.REPEATED_CALLS_REPORT:
        // Repeated calls report requires month
        if (!payload.from || !payload.to) missing.push('date range (from/to)');
        break;
    }
    
    return {
      valid: missing.length === 0,
      missing
    };
  };
  
  const preloadReportData = async (reportType, stateManager) => {
    // Set default date range (last 24 hours) for preloading
    const now = moment();
    
    const defaultPayload = {
      from: now.startOf('day').format('YYYY-MM-DD HH:mm:ss'),
      to: now.endOf('day').format('YYYY-MM-DD HH:mm:ss'),
      callType: 'All',
      agent: 'All',
      hungparty: 'All',
      number: '',
      duration: '',
      skill: 0, // Skill will be set by the UI component when skills are loaded
      period: 2,
      agentStatus: 'All',
      export: false,
      offset: 0,
      limit: stateManager.recordsPerPage.value,
      iscount: 1
    };
    
    try {
      await fetchReportData(reportType, defaultPayload, stateManager);
    } catch (error) {
      // Silently fail for preloading - don't show error to user
    }
  };
  
  return {
    fetchReportData,
    fetchReportDataWithRetry,
    preloadReportData,
    validateRequiredFilters,
    reportActions,
    dataExtractors
  };
} 